import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Error Boundary Component
 * Catches JavaScript errors in child components and displays a fallback UI
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({ errorInfo });
    
    // Log to external service if configured
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService(error, errorInfo) {
    // Send to Sentry or other error tracking service
    // This would be configured via environment variables
    const analyticsEnabled = process.env.REACT_APP_ENABLE_ANALYTICS === 'true';
    
    if (analyticsEnabled && window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: true
      });
    }
    
    // You can also send to a custom endpoint
    // fetch('/api/log-error', { ... })
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-icon">⚠️</div>
            <h1>Something went wrong</h1>
            <p>We apologize for the inconvenience. An unexpected error has occurred.</p>
            
            <div className="error-actions">
              <button 
                className="btn-primary"
                onClick={this.handleRetry}
              >
                Try Again
              </button>
              <Link to="/" className="btn-secondary">
                Go to Homepage
              </Link>
            </div>
            
            {isDevelopment && this.state.error && (
              <div className="error-details">
                <h3>Error Details (Development Only)</h3>
                <pre>{this.state.error.toString()}</pre>
                {this.state.errorInfo && (
                  <pre>{this.state.errorInfo.componentStack}</pre>
                )}
              </div>
            )}
          </div>
          
          <style jsx="true">{`
            .error-boundary {
              min-height: 60vh;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 40px 20px;
              background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
            }
            
            .error-boundary-content {
              text-align: center;
              max-width: 500px;
            }
            
            .error-icon {
              font-size: 4rem;
              margin-bottom: 20px;
            }
            
            .error-boundary h1 {
              color: #fff;
              font-size: 2rem;
              margin-bottom: 15px;
            }
            
            .error-boundary p {
              color: rgba(255, 255, 255, 0.7);
              margin-bottom: 30px;
            }
            
            .error-actions {
              display: flex;
              gap: 15px;
              justify-content: center;
              flex-wrap: wrap;
            }
            
            .error-actions .btn-primary {
              padding: 12px 24px;
              background: linear-gradient(135deg, #FE5500, #ff7a00);
              border: none;
              border-radius: 8px;
              color: #fff;
              font-weight: 500;
              cursor: pointer;
              text-decoration: none;
            }
            
            .error-actions .btn-secondary {
              padding: 12px 24px;
              background: transparent;
              border: 1px solid rgba(255, 255, 255, 0.3);
              border-radius: 8px;
              color: #fff;
              font-weight: 500;
              cursor: pointer;
              text-decoration: none;
            }
            
            .error-details {
              margin-top: 40px;
              text-align: left;
              background: rgba(0, 0, 0, 0.5);
              padding: 20px;
              border-radius: 8px;
              overflow: auto;
              max-height: 300px;
            }
            
            .error-details h3 {
              color: #FE5500;
              margin-bottom: 15px;
              font-size: 1rem;
            }
            
            .error-details pre {
              color: rgba(255, 255, 255, 0.8);
              font-size: 0.8rem;
              white-space: pre-wrap;
              word-break: break-word;
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * withErrorBoundary HOC
 * Wraps a component with an error boundary
 */
export function withErrorBoundary(WrappedComponent, fallback = null) {
  return function WithErrorBoundaryWrapper(props) {
    return (
      <ErrorBoundary fallback={fallback}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

export default ErrorBoundary;
